import _ from 'lodash'
import moment from 'moment'
import { useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'

import api from '../../services/api'
import { ExternalClickContext } from '../../contexts'
import useModal from '../../hooks/useModal'

import * as S from './Planner.styles'
import Tooltip from '@mui/material/Tooltip'
import { SmallNumberInput } from '../../components/InputBoxes'

export default function WeeklyPlan() {
  const externalClickEvent = useContext(ExternalClickContext)
  const { openModal, closeModal } = useModal()
  const { sessionalPlanId } = useParams()
  const mounted = useRef(false)
  const [query, setQuery] = useSearchParams()
  const [weeklyPlan, setWeeklyPlan] = useState()
  const weekStartOn = query.get('weekStartOn')
  const weekEndOn = query.get('weekEndOn')

  useEffect(() => {
    if (! mounted.current) {
      mounted.current = true
      api.get(`/sessional-plans/${sessionalPlanId}/weekly-plans`
        , {params: {weekStartOn, weekEndOn}})
        .then(result => setWeeklyPlan(result.data))
        .catch(e => {
          console.error(e)
          openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message }})
        })
    } else {
      if (! externalClickEvent) return
      externalClickEvent.stopPropagation() // 이벤트 캡쳐링 방지
      if (! externalClickEvent.target.matches('input')) {
        weeklyPlan.weeklyQuantityList.forEach(wq => {
          wq.dailyQuantityList.forEach(dq => dq.isModifyCompledQuantity = false)
        })
        setWeeklyPlan(prev => ({ ...prev }))
      }
    }
  }, [externalClickEvent])

  /**
   * 계획 달성량 수정하기를 클릭 시 input 태그가 열리도록 한다.
   */
  function clickCompletedQuantityUpdate(weeklyQuantityId, dailyQuantityId, e) {
    e.stopPropagation() // 이벤트 캡쳐링 방지
    const weeklyQuantityOrder = _.findIndex(weeklyPlan.weeklyQuantityList, weeklyQuantity => weeklyQuantity.weeklyQuantityId === weeklyQuantityId)
    const dailyQuantityOrder = _.findIndex(weeklyPlan.weeklyQuantityList[weeklyQuantityOrder].dailyQuantityList, dailyQuantity => dailyQuantity.dailyQuantityId === dailyQuantityId)
    weeklyPlan.weeklyQuantityList[weeklyQuantityOrder].dailyQuantityList[dailyQuantityOrder].isModifyCompledQuantity = true
    setWeeklyPlan({...weeklyPlan})
  }

  /**
   * 다른 영역 클릭 시 input 태그가 닫히도록 한다.
   */
  function endInput(e) {
    e.stopPropagation() // 이벤트 캡쳐링 방지
    if (! e.target.matches('input')) {
      weeklyPlan.weeklyQuantityList.forEach(wq => {
        wq.dailyQuantityList.forEach(dq => dq.isModifyCompledQuantity = false)
      })
      setWeeklyPlan({...weeklyPlan})
    }
  }

  function drag(e) {
    e.dataTransfer.setData('dailyQuantity', e.target.id)
  }

  function drop(e) {
    e.preventDefault()
    const target = JSON.parse(e.target.id)
    const dailyQuantity = JSON.parse(e.dataTransfer.getData('dailyQuantity'))
    openModal({
      type: 'updateDailyDelay',
      props: {
        sessionalPlanId,
        totalQuantityId: dailyQuantity.totalQuantityId,
        weeklyQuantityId: dailyQuantity.weeklyQuantityId,
        dailyQuantityId: dailyQuantity.dailyQuantityId,
        delayedQuantityAmount: dailyQuantity.delayedQuantityAmount,
        targetDailyQuantityId: target.dailyQuantityId,
        targetDayOfWeek: target.dayOfWeek,
        weeklyPlan,
        setWeeklyPlan,
      }
    })
  }

  async function updateCompletedQuantity(sessionalPlanId, totalQuantityId, weeklyQuantityId, dailyQuantityId, dailyQuantity, completedQuantity) {
    if (completedQuantity < 0) {
      return openModal({ type: 'alert', props: { isError: true, contents: '0보다 크거나 같은 숫자만 허용됩니다' }})
    }

    if (dailyQuantity < completedQuantity) {
      return openModal({ type: 'alert', props: { isError: true, contents: '계획량보다 큰 값이 존재해선 안 됩니다' }})
    }

    await api.patch(`/sessional-plans/${sessionalPlanId}/total-quantities/${totalQuantityId}/weekly-quantities/${weeklyQuantityId}/daily-quantities/${dailyQuantityId}/completed-quantities`
      , { completedQuantityAmount: completedQuantity })
      .then(result => {
        const weeklyQuantityOrder = _.findIndex(weeklyPlan.weeklyQuantityList, weeklyQuantity => weeklyQuantity.weeklyQuantityId === weeklyQuantityId)
        const dailyQuantityOrder = _.findIndex(weeklyPlan.weeklyQuantityList[weeklyQuantityOrder].dailyQuantityList, dailyQuantity => dailyQuantity.dailyQuantityId === dailyQuantityId)
        weeklyPlan.weeklyQuantityList[weeklyQuantityOrder].dailyQuantityList[dailyQuantityOrder] = result.data
        setWeeklyPlan({...weeklyPlan})
      })
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message }})
      })
  }

  return (
    <div style={{ margin: '30px', position: 'relative', minHeight: '100vh' }} onClick={(e) => endInput(e)}>
      <h2>Weekly Plan</h2>
      <span
        style={{
          fontWeight: 'bold',
          position: 'absolute',
          right: '0px',
          top: '0px',
        }}
      >
        {weeklyPlan?.date.weekNum}주 차
        ({weeklyPlan?.date.weekStartOn} / {weeklyPlan?.date.weekEndOn})
      </span>
      {/* 세셔널 플랜 본문 부분 */}
      <div style={{ marginTop: '30px', overflowX: 'scroll' }}>
        <S.Planner>
          {/* 헤더 - 분류, 영역, session 분량, 주 차 */}
          <tr>
            <S.MainHeader rowSpan='2' width='50px'>분류</S.MainHeader>
            <S.MainHeader rowSpan='2'>
              <span style={{ verticalAlign: 'middle' }}>영역</span>
            </S.MainHeader>
            <S.MainHeader rowSpan='2'>week 분량</S.MainHeader>
            {
              _.map(weeklyPlan?.date.dayOfWeekList, dayOfWeek  => {
                return (
                  <S.MainHeader
                    colSpan='3'
                    fontSize='0.75em'
                    width='170px'
                    height='25px'>
                    {dayOfWeek.dayOfWeek} &nbsp;
                    ({moment(dayOfWeek?.date).format('MM / DD')})
                  </S.MainHeader>
                )
              })
            }
          </tr>
          {/* 헤더 - 주 차별 분량, 완료, 딜레이 */}
          <tr>
            {
              _.map(weeklyPlan?.date.dayOfWeekList, () => {
                return (
                  <>
                    <S.SubHeader>분량</S.SubHeader>
                    <S.SubHeader>완료</S.SubHeader>
                    <S.SubHeader style={{ backgroundColor: '#FFC8E2' }}>딜레이</S.SubHeader>
                  </>
                )
              })
            }
          </tr>
          {
            _.entries(_.groupBy(_.filter(weeklyPlan?.weeklyQuantityList, wq => wq.weeklyQuantityAmount), 'subjectCategory'))
              .map(([subjectCategory, subjectList]) => {
                return (
                  _.map(subjectList, (subject, subjectOrder) => {
                    return (
                      <tr>
                        {/* 분류 */}
                        {
                          subjectOrder === 0
                            ? <S.TotalPlanData title={subjectCategory} rowSpan={subjectList.length}>{subjectCategory}</S.TotalPlanData>
                            :  <></>
                        }
                        {/* 영역 */}
                        <S.TotalPlanData title={subject.subject}>{subject.subject}</S.TotalPlanData>
                        {/* week 분량 */}
                        <Tooltip
                          title={subject.weeklyQuantityMemo}
                          arrow
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -25],
                                  },
                                },
                              ],
                            },
                          }}
                        >
                          <S.TotalPlanData>{`${subject.weeklyQuantityAmount}${subject.unit}`}</S.TotalPlanData>
                        </Tooltip>
                        {/* 일별 계획 양, 완수 양, 딜레이 양 */}
                        {
                          _.map(subject.dailyQuantityList, (dq, dqOrder) => {
                            return (
                              <>
                                <Tooltip
                                  title={dq.dailyQuantityMemo}
                                  arrow
                                  slotProps={{
                                    popper: {
                                      modifiers: [
                                        {
                                          name: 'offset',
                                          options: {
                                            offset: [0, -25],
                                          },
                                        },
                                      ],
                                    },
                                  }}
                                >
                                  <S.Data
                                    id={`{ "dailyQuantityId": ${dq.dailyQuantityId}, "dayOfWeek": "${_.filter(weeklyPlan?.date.dayOfWeekList, dayOfWeek => dayOfWeek.date === dq.date)[0].dayOfWeek }" }`}
                                    onClick={() => openModal({
                                      type: 'updateQuantity',
                                      props: {
                                        quantity: dq.dailyQuantityAmount,
                                        completedQuantity: dq.completedQuantityAmount,
                                        memo: dq.dailyQuantityMemo,
                                        callbackFun: async (data) => {
                                            try {
                                              await api.put(`sessional-plans/${sessionalPlanId}/total-quantities/${subject.totalQuantityId}/weekly-quantities/${subject.weeklyQuantityId}/daily-quantities/${dq.dailyQuantityId}`
                                                , { reducedDailyQuantity: data.quantity, dailyQuantityMemo: data.memo })
                                                .then(result => {
                                                  weeklyPlan.weeklyQuantityList.forEach(wq => {
                                                    if (wq.weeklyQuantityId === subject.weeklyQuantityId) wq.dailyQuantityList = result.data.dailyQuantityList
                                                  })
                                                  setWeeklyPlan(prev => ({ ...prev }))
                                                  closeModal()
                                                })
                                            } catch (err) {
                                              console.error(err)
                                              openModal({ type: 'alert', props: { isError: true, contents: err.response?.data.message || err.message }})
                                            }
                                          }
                                        }
                                      })
                                    }
                                    onDrop={drop}
                                    onDragOver={(e) => e.preventDefault()}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {dq.dailyQuantityAmount}
                                  </S.Data>
                                </Tooltip>
                                <S.Data onClick={(e) => clickCompletedQuantityUpdate(subject.weeklyQuantityId, dq.dailyQuantityId, e)} style={{ cursor: 'pointer' }}>
                                  {
                                    dq.isModifyCompledQuantity
                                    ? <SmallNumberInput
                                        number={dq.completedQuantityAmount}
                                        onBlur={(e) => updateCompletedQuantity(sessionalPlanId, subject.totalQuantityId, subject.weeklyQuantityId, dq.dailyQuantityId, dq.dailyQuantityList, e.target.value)}
                                      />
                                    : dq.completedQuantityAmount
                                  }
                                </S.Data>
                                <S.Data
                                  id={`{ "dailyQuantityId": ${dq.dailyQuantityId}, "totalQuantityId": ${subject.totalQuantityId}, "weeklyQuantityId": ${subject.weeklyQuantityId}, "delayedQuantityAmount": ${dq.delayedQuantityAmount} }`}
                                  draggable={true}
                                  onDragStart={drag}
                                  style={{ backgroundColor: '#FFEFF5', cursor: 'move' }}
                                >
                                  {dq.delayedQuantityAmount}
                                </S.Data>
                              </>
                            )
                          })
                        }
                      </tr>
                    )
                  })
                )
              })
          }
        </S.Planner>
      </div>
    </div>
  )
}